import { useState } from "react";
import {
  Box,
  Grid,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SaveIcon from "@mui/icons-material/Save";
import { updateToastError } from "../utils/notification-loading";

// Ordered to match the grid's column order (hostsOnlineGrid.jsx `assetColumns`) so the
// filter picker lists fields in the same sequence a user scans them across the table.
const filterFields = [
  {
    field: "pref",
    headerName: "FAVOURITE",
    type: "boolean",
    operators: ["equals"],
  },
  {
    field: "status",
    headerName: "STATUS",
    type: "status",
    operators: ["equals"],
  },
  {
    field: "asset_type",
    headerName: "ASSET TYPE",
    type: "asset_type",
    operators: ["equals"],
  },
  {
    // Matches any of the three classification levels, so "ot", "control" and "plc"
    // all reach the same asset without the user having to know which level a value
    // lives on.
    field: "classification",
    headerName: "CLASSIFICATION",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "hostname",
    headerName: "HOSTNAME",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "ip",
    headerName: "IP ADDRESS",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "mac",
    headerName: "MAC ADDRESS",
    type: "text",
    operators: ["contains", "equals", "notEquals"],
  },
  {
    field: "vendor",
    headerName: "VENDOR",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "os_family",
    headerName: "OS FAMILY",
    type: "os_family",
    operators: ["contains", "notContains"],
  },
  {
    field: "vlan",
    headerName: "VLAN",
    type: "text",
    operators: ["equals", "notEquals"],
  },
  {
    // Filters on dynamic_risk_score (falling back to the static risk_score) — the
    // same field the grid's RISK column renders, so a "Critical" filter here always
    // matches what the column shows. See matchFilter() in assetUtils.js.
    field: "risk_score",
    headerName: "RISK",
    type: "risk",
    operators: ["equals"],
  },
  {
    field: "risk_severity",
    headerName: "CVE SEVERITY",
    type: "severity",
    operators: ["equals"],
  },
  {
    field: "cve_id",
    headerName: "CVE ID",
    type: "text",
    operators: ["contains", "equals"],
  },
];

const operatorLabels = {
  contains: "contains",
  notContains: "not contains",
  equals: "equals",
  notEquals: "not equals",
  gte: "at least",
  lte: "at most",
};

function defaultValue(fieldDef) {
  if (fieldDef.type === "status") return "online";
  if (fieldDef.type === "asset_type") return "it";
  if (fieldDef.type === "boolean") return "1";
  if (fieldDef.type === "severity") return "critical";
  return "";
}

function getFiltersWithoutLogic(list) {
  if (list.length > 0 && list[0].logic) {
    return list.map(({ logic, ...rest }) => rest);
  }
  return list;
}

const ValueInput = ({ filter, index, onChange }) => {
  const fieldDef = filterFields.find((f) => f.field === filter.field);
  if (!fieldDef) return null;

  if (fieldDef.type === "status") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value || "online"}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        <MenuItem value="online">ONLINE</MenuItem>
        <MenuItem value="offline">OFFLINE</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "asset_type") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value || "it"}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        <MenuItem value="it">IT</MenuItem>
        <MenuItem value="ot">OT</MenuItem>
        <MenuItem value="iot">IoT</MenuItem>
        <MenuItem value="iomt">IoMT</MenuItem>
        {/* A real taxonomy answer ("looked, could not place it"), not a missing value */}
        <MenuItem value="unclassified">UNCLASSIFIED</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "os_family") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value || "Windows"}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        <MenuItem value="Windows">WINDOWS</MenuItem>
        <MenuItem value="Linux">LINUX</MenuItem>
        <MenuItem value="Unix">UNIX</MenuItem>
        <MenuItem value="macOS">MACOS</MenuItem>
        <MenuItem value="Android">ANDROID</MenuItem>
        <MenuItem value="iOS">IOS</MenuItem>
        <MenuItem value="Embedded">EMBEDDED</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "boolean") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value ?? "1"}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        <MenuItem value="1">YES</MenuItem>
        <MenuItem value="0">NO</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "severity") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value || "critical"}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        <MenuItem value="critical">CRITICAL</MenuItem>
        <MenuItem value="high">HIGH</MenuItem>
        <MenuItem value="medium">MEDIUM</MenuItem>
        <MenuItem value="low">LOW</MenuItem>
        <MenuItem value="none">NONE</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "risk") {
    const bands = [
      { value: "critical", label: "Critical", color: "#ff4444", bg: "#ff222233" },
      { value: "high", label: "High", color: "#ff7744", bg: "#ff552233" },
      { value: "elevated", label: "Elevated", color: "#ffb422", bg: "#ffb42233" },
      { value: "moderate", label: "Moderate", color: "#7b8bff", bg: "#4254fb33" },
      { value: "low", label: "Low", color: "#4cceac", bg: "#4cceac22" },
    ];
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, py: 0.5 }}>
        {bands.map((band) => (
          <Chip
            key={band.value}
            label={band.label}
            size="small"
            onClick={() => onChange(index, "value", band.value)}
            variant={filter.value === band.value ? "filled" : "outlined"}
            sx={{ bgcolor: band.bg, color: band.color, borderColor: `${band.color}88`, fontWeight: 700, fontSize: 10.5 }}
          />
        ))}
      </Box>
    );
  }

  if (fieldDef.type === "number") {
    return (
      <TextField
        variant="standard"
        type="number"
        fullWidth
        placeholder={fieldDef.headerName}
        value={filter.value || ""}
        onChange={(e) => onChange(index, "value", e.target.value)}
      />
    );
  }

  return (
    <TextField
      variant="standard"
      fullWidth
      placeholder={fieldDef.headerName}
      value={filter.value || ""}
      onChange={(e) => onChange(index, "value", e.target.value)}
    />
  );
};

const AssetCustomFilters = ({ filterModel, setFilterModel }) => {
  const [filtersList, setFiltersList] = useState(getFiltersWithoutLogic(filterModel));

  const handleChange = (index, key, value) => {
    const updated = [...filtersList];
    if (key === "field") {
      const fieldDef = filterFields.find((f) => f.field === value);
      updated[index] = {
        ...updated[index],
        field: value,
        operator: fieldDef.operators[0],
        value: defaultValue(fieldDef),
      };
    } else {
      updated[index] = { ...updated[index], [key]: value };
    }
    setFiltersList(updated);
  };

  const handleAddFilter = () => {
    const fieldDef = filterFields[0];
    const newFilter = {
      field: fieldDef.field,
      operator: fieldDef.operators[0],
      value: defaultValue(fieldDef),
    };
    if (filtersList.length > 0) newFilter.logic = "and";
    setFiltersList((prev) => [...prev, newFilter]);
  };

  const handleDelete = (index) => {
    let newList = filtersList.filter((_, i) => i !== index);
    if (index === 0 && newList.length > 0) {
      const { logic, ...rest } = newList[0];
      newList[0] = rest;
    }
    setFiltersList(newList);
  };

  const handleRemoveAll = () => {
    setFiltersList([]);
    setFilterModel([]);
  };

  const handleSave = () => {
    for (const f of filtersList) {
      if (f.value === null || f.value === undefined || f.value === "") {
        updateToastError("Invalid filter value");
        return;
      }
    }
    setFilterModel(filtersList);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "55vw" }}>
      {filtersList.length > 0 ? (
        filtersList.map((filter, index) => {
          const fieldDef = filterFields.find((f) => f.field === filter.field);
          return (
            <Box key={index} sx={{ width: "100%" }}>
              <Grid container spacing={2} alignItems="center" sx={{ padding: 2 }}>
                <Grid size={0.5}>
                  <IconButton color="error" onClick={() => handleDelete(index)} sx={{ minWidth: 0, padding: "6px" }}>
                    <ClearIcon />
                  </IconButton>
                </Grid>

                {filter.logic ? (
                  <Grid size={1.2}>
                    <Select
                      variant="standard"
                      fullWidth
                      value={filter.logic}
                      onChange={(e) => handleChange(index, "logic", e.target.value)}
                    >
                      <MenuItem value="and">AND</MenuItem>
                      <MenuItem value="or">OR</MenuItem>
                    </Select>
                  </Grid>
                ) : filtersList.length > 1 ? (
                  <Grid size={1.2} />
                ) : null}

                <Grid size={filtersList.length > 1 ? 2.8 : 3.5}>
                  <Select
                    variant="standard"
                    fullWidth
                    value={filter.field || ""}
                    onChange={(e) => handleChange(index, "field", e.target.value)}
                  >
                    {filterFields.map((f) => (
                      <MenuItem key={f.field} value={f.field}>{f.headerName}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid size={2}>
                  <Select
                    variant="standard"
                    fullWidth
                    value={filter.operator || ""}
                    onChange={(e) => handleChange(index, "operator", e.target.value)}
                  >
                    {(fieldDef?.operators || []).map((op) => (
                      <MenuItem key={op} value={op}>{operatorLabels[op] ?? op}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid size={5}>
                  <ValueInput filter={filter} index={index} onChange={handleChange} />
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        <Typography sx={{ fontSize: 16, fontFamily: "poppins", py: 2 }}>No filters</Typography>
      )}

      <Grid container spacing={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: 1, width: "100%" }}>
        <Grid size={4}>
          <Button fullWidth sx={{ fontFamily: "poppins", fontSize: 12 }} onClick={handleAddFilter}>
            <PlaylistAddIcon sx={{ marginRight: 1 }} /> ADD FILTER
          </Button>
        </Grid>
        <Grid size={4} display="flex" gap={2} justifyContent="flex-end">
          <Button
            color="error"
            variant="contained"
            disabled={filtersList.length === 0}
            fullWidth
            onClick={handleRemoveAll}
            sx={{ fontFamily: "poppins", fontSize: 12, "&:hover": { backgroundColor: "#f44336 !important", opacity: 0.8 } }}
          >
            <DeleteForeverIcon fontSize="small" sx={{ marginRight: 1 }} /> REMOVE ALL
          </Button>
          <Button fullWidth sx={{ fontFamily: "poppins", fontSize: 12 }} onClick={handleSave}>
            <SaveIcon fontSize="small" sx={{ marginRight: 1 }} /> APPLY FILTER
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssetCustomFilters;
